import React, { useEffect, useMemo, useState } from 'react';
import NewsletterSignup from '../components/NewsletterSignup';

type GradeBand = '6–8' | '9–10' | '11–12';

const BANDS: GradeBand[] = ['6–8', '9–10', '11–12'];

interface CalendarItem {
  window: string;
  title: string;
  detail: string;
  bands: GradeBand[];
}

interface CalendarMonth {
  month: string;
  items: CalendarItem[];
}

// Deadline data is reviewed each cycle — dates shift slightly year to year,
// so entries use windows ("early Nov") rather than exact days where possible.
const CALENDAR: CalendarMonth[] = [
  {
    month: 'September',
    items: [
      {
        window: 'Early Sep',
        title: 'FTC season kickoff (game reveal)',
        detail: 'Teams form and register now — the easiest month of the year to join robotics.',
        bands: ['6–8', '9–10', '11–12'],
      },
      {
        window: 'All month',
        title: 'AMC 10/12 registration opens (through your school)',
        detail: 'Ask the math department now; schools that miss registration lock students out of the whole AIME path for the year.',
        bands: ['9–10', '11–12'],
      },
      {
        window: 'All month',
        title: 'Science Olympiad team registration',
        detail: 'State-dependent windows — most schools finalize rosters in early fall.',
        bands: ['6–8', '9–10', '11–12'],
      },
      {
        window: 'All month',
        title: 'Research lab outreach window (spring/summer positions)',
        detail: 'Professors plan spring semesters now. Cold emails sent in September get answered; ones sent in January compete with finals.',
        bands: ['9–10', '11–12'],
      },
    ],
  },
  {
    month: 'October',
    items: [
      {
        window: 'Mid Oct',
        title: 'AMC 10/12 early-bird registration deadline',
        detail: 'Lower fees, and the safest date to confirm your school actually registered.',
        bands: ['9–10', '11–12'],
      },
      {
        window: 'All month',
        title: 'MATHCOUNTS school registration',
        detail: 'Middle-school math’s main event — a school coordinator must register the team.',
        bands: ['6–8'],
      },
      {
        window: 'Late Oct',
        title: 'FLL regional qualifier registration closes (many regions)',
        detail: 'Check your region’s FIRST calendar — qualifier slots fill before the deadline.',
        bands: ['6–8'],
      },
    ],
  },
  {
    month: 'November',
    items: [
      {
        window: 'Early Nov',
        title: 'AMC 10A / 12A competition day',
        detail: 'First of the two dates. Students may take both A and B versions.',
        bands: ['9–10', '11–12'],
      },
      {
        window: 'Mid Nov',
        title: 'AMC 10B / 12B competition day',
        detail: 'Second chance at AIME qualification in the same season.',
        bands: ['9–10', '11–12'],
      },
      {
        window: 'Mid Nov',
        title: 'Regeneron Science Talent Search application due',
        detail: 'Seniors with completed research only — the application is substantial, start it in September.',
        bands: ['11–12'],
      },
      {
        window: 'All month',
        title: 'FTC league meets / qualifiers begin',
        detail: 'Competition season starts — advancement runs through February.',
        bands: ['6–8', '9–10', '11–12'],
      },
    ],
  },
  {
    month: 'December',
    items: [
      {
        window: 'Mid Dec',
        title: 'USACO December contest',
        detail: 'First of four contests in the season. New competitors start in Bronze — anyone can enter, free.',
        bands: ['6–8', '9–10', '11–12'],
      },
      {
        window: 'All month',
        title: 'FLL regional qualifiers',
        detail: 'Most regions run December–January events.',
        bands: ['6–8'],
      },
      {
        window: 'All month',
        title: 'Summer research program applications open',
        detail: 'The selective free programs (SSP, RSI-type) open now with January–March deadlines.',
        bands: ['9–10', '11–12'],
      },
    ],
  },
  {
    month: 'January',
    items: [
      {
        window: 'First Sat',
        title: 'FRC kickoff — game reveal and build season start',
        detail: 'Six intense weeks begin. The best month for new members to see what the team really is.',
        bands: ['9–10', '11–12'],
      },
      {
        window: 'Late Jan',
        title: 'AMC 8 competition week',
        detail: 'The middle-school AMC — low stakes, and the best early benchmark for the math path.',
        bands: ['6–8'],
      },
      {
        window: 'Late Jan',
        title: 'USACO January contest',
        detail: 'Second contest of the season; promotion between divisions happens per contest.',
        bands: ['6–8', '9–10', '11–12'],
      },
      {
        window: 'All month',
        title: 'F=ma registration deadline',
        detail: 'The entry exam for the physics olympiad path — registration runs through schools or approved centers.',
        bands: ['9–10', '11–12'],
      },
    ],
  },
  {
    month: 'February',
    items: [
      {
        window: 'Early Feb',
        title: 'AIME I',
        detail: 'Qualified via AMC 10/12 scores. AIME II follows about two weeks later for date conflicts.',
        bands: ['9–10', '11–12'],
      },
      {
        window: 'Mid Feb',
        title: 'F=ma exam day',
        detail: 'Top scorers advance to USAPhO in April.',
        bands: ['9–10', '11–12'],
      },
      {
        window: 'All month',
        title: 'MATHCOUNTS chapter competitions',
        detail: 'The first advancement round — top individuals and teams move to state.',
        bands: ['6–8'],
      },
      {
        window: 'Late Feb',
        title: 'USACO February contest',
        detail: 'Third contest of the season.',
        bands: ['6–8', '9–10', '11–12'],
      },
      {
        window: 'All month',
        title: 'Regional science fairs (ISEF-affiliated)',
        detail: 'Regional → state → ISEF. Paperwork (SRC forms) must be approved before you compete.',
        bands: ['6–8', '9–10', '11–12'],
      },
    ],
  },
  {
    month: 'March',
    items: [
      {
        window: 'Mid Mar',
        title: 'USAMO / USAJMO',
        detail: 'Invitation computed from AMC + AIME index. Roughly 250–500 qualifiers nationally per exam.',
        bands: ['9–10', '11–12'],
      },
      {
        window: 'Late Mar',
        title: 'USACO US Open',
        detail: 'Season finale — Platinum results here feed training-camp selection.',
        bands: ['6–8', '9–10', '11–12'],
      },
      {
        window: 'All month',
        title: 'MATHCOUNTS state competitions',
        detail: 'Top four individuals per state form the national team.',
        bands: ['6–8'],
      },
      {
        window: 'All month',
        title: 'State science fairs / ISEF affiliate finals',
        detail: 'ISEF spots are awarded here — check your affiliate fair’s specific date early.',
        bands: ['9–10', '11–12'],
      },
      {
        window: 'All month',
        title: 'Science Olympiad regionals and states',
        detail: 'Schedules vary by state; most run February–April.',
        bands: ['6–8', '9–10', '11–12'],
      },
    ],
  },
  {
    month: 'April',
    items: [
      {
        window: 'Early Apr',
        title: 'USAPhO exam',
        detail: 'Qualified via F=ma. Top ~20 advance to physics camp.',
        bands: ['9–10', '11–12'],
      },
      {
        window: 'Mid Apr',
        title: 'FIRST Championship (FRC + FTC Worlds)',
        detail: 'Season culmination — travel plans and fundraising need to be settled well before advancement is confirmed.',
        bands: ['6–8', '9–10', '11–12'],
      },
    ],
  },
  {
    month: 'May',
    items: [
      {
        window: 'Mid May',
        title: 'Regeneron ISEF',
        detail: 'The international finals for the science-fair path.',
        bands: ['9–10', '11–12'],
      },
      {
        window: 'Mid May',
        title: 'MATHCOUNTS national competition',
        detail: 'State team qualifiers only.',
        bands: ['6–8'],
      },
      {
        window: 'All month',
        title: 'VEX Robotics World Championship',
        detail: 'Both middle- and high-school divisions.',
        bands: ['6–8', '9–10', '11–12'],
      },
      {
        window: 'Late May',
        title: 'Science Olympiad national tournament',
        detail: 'Two teams per state qualify.',
        bands: ['6–8', '9–10', '11–12'],
      },
    ],
  },
  {
    month: 'June',
    items: [
      {
        window: 'All month',
        title: 'Olympiad training camps (MOP, physics camp)',
        detail: 'By invitation from spring olympiad results.',
        bands: ['9–10', '11–12'],
      },
      {
        window: 'All month',
        title: 'Next season planning window',
        detail: 'The quiet month: audit this year’s activities, book mentor conversations, and decide what to go deeper on before fall locks the schedule.',
        bands: ['6–8', '9–10', '11–12'],
      },
    ],
  },
];

const DeadlineCalendarPage: React.FC = () => {
  const [activeBand, setActiveBand] = useState<GradeBand | 'all'>('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filtered = useMemo(() => {
    if (activeBand === 'all') return CALENDAR;
    return CALENDAR.map((m) => ({
      ...m,
      items: m.items.filter((item) => item.bands.includes(activeBand)),
    })).filter((m) => m.items.length > 0);
  }, [activeBand]);

  return (
    <>
      {/* Header */}
      <header className="section" style={{ paddingBottom: 48 }}>
        <div className="wrap">
          <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
            Deadline calendar · free
          </span>
          <h1 style={{ maxWidth: '26ch' }}>Every major competition deadline, in one place.</h1>
          <p style={{ color: 'var(--ink-secondary)', marginTop: 16, maxWidth: '62ch' }}>
            Registration windows and competition dates for the major math, science, computing,
            and robotics pipelines — organized by month so nothing sneaks up on your family.
            Filter by your student’s grade band.
          </p>
          <p className="meta" style={{ marginTop: 12, maxWidth: '62ch' }}>
            Exact dates shift slightly each year and some vary by state or region — always
            confirm on the official competition site before planning travel.
          </p>
        </div>
      </header>

      {/* Calendar */}
      <section className="section warm" style={{ paddingTop: 48 }}>
        <div className="wrap" style={{ maxWidth: 920 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 28 }}>
            <button
              className={`chip${activeBand === 'all' ? ' on' : ''}`}
              onClick={() => setActiveBand('all')}
              type="button"
            >
              All grades
            </button>
            {BANDS.map((band) => (
              <button
                key={band}
                className={`chip${activeBand === band ? ' on' : ''}`}
                onClick={() => setActiveBand(band)}
                type="button"
              >
                Grades {band}
              </button>
            ))}
          </div>

          {filtered.map((month) => (
            <div key={month.month} className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ marginBottom: 12 }}>{month.month}</h3>
              <table className="data">
                <tbody>
                  {month.items.map((item) => (
                    <tr key={item.title}>
                      <td className="meta" style={{ whiteSpace: 'nowrap', width: 90 }}>
                        {item.window}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{item.title}</div>
                        <div className="meta" style={{ marginBottom: 6 }}>{item.detail}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {item.bands.map((band) => (
                            <span key={band} className="tag solid" style={{ fontSize: 11.5 }}>
                              Grades {band}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </section>

      <NewsletterSignup
        variant="band"
        source="deadline-calendar"
        heading="Never miss a registration window."
        subtext="Subscribe and the weekly email flags the deadlines coming up in the next eight weeks — free, no spam, unsubscribe anytime."
      />
    </>
  );
};

export default DeadlineCalendarPage;
